import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/database';
import { Session } from '@supabase/supabase-js';
import { validateEmail, validatePassword, getAuthErrorMessage } from '../utils/auth-validation';
import { AUTH_MESSAGES, AUTH_ROUTES } from '../utils/auth-constants';
import { logger } from '../utils/logger';

interface User {
  email: string;
  name: string;
  picture: string;
  sub: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  login: (response: any) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, name?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    // Get initial session and handle OAuth callback
    const initializeAuth = async () => {
      try {
        logger.log('Initializing auth...');
        
        // Check if this is an OAuth callback
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('Error getting session:', error);
          if (mounted) setIsLoading(false);
          return;
        }

        logger.log('Got session:', data.session?.user?.id);
        
        // Check for email verification success
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('verified') === 'true') {
          logger.log('Email verification detected');
          // Clear the URL parameter
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('verified');
          window.history.replaceState({}, '', newUrl);
        }
        
        if (mounted && data.session) {
          setSession(data.session);
          const userData: User = {
            email: data.session.user.email || '',
            name: data.session.user.user_metadata.full_name || data.session.user.user_metadata.name || '',
            picture: data.session.user.user_metadata.avatar_url || data.session.user.user_metadata.picture || '',
            sub: data.session.user.id,
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          
          // Handle redirect after successful login
          const redirectTo = localStorage.getItem('redirectTo');
          if (redirectTo && redirectTo !== '/login') {
            localStorage.removeItem('redirectTo');
            logger.log('Redirecting to:', redirectTo);
            navigate(redirectTo, { replace: true });
          } else if (location.pathname === '/login') {
            // Check if user came from magnet page via URL parameter (more reliable than localStorage)
            const urlParams = new URLSearchParams(location.search);
            const fromMagnetUrl = urlParams.get('fromMagnet');
            const fromMagnetStorage = localStorage.getItem('fromMagnetPage');
            
            logger.log('AuthContext (initializeAuth) - fromMagnet URL param:', fromMagnetUrl);
            logger.log('AuthContext (initializeAuth) - fromMagnetPage storage:', fromMagnetStorage);
            
            if (fromMagnetUrl === 'true' || fromMagnetStorage === 'true') {
              logger.log('AuthContext (initializeAuth) - Redirecting to PDF processor');
              localStorage.removeItem('fromMagnetPage');
              navigate('/dashboard/processor', { replace: true });
            } else {
              logger.log('AuthContext (initializeAuth) - Redirecting to main processor');
              navigate('/dashboard/processor', { replace: true });
            }
          }
        }
      } catch (error) {
        logger.error('Error in initializeAuth:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.log('Auth state changed:', event, session?.user?.id);

      if (mounted) {
        if (event === 'SIGNED_IN' && session) {
          try {
            const userData: User = {
              email: session.user.email || '',
              name: session.user.user_metadata.full_name || session.user.user_metadata.name || '',
              picture: session.user.user_metadata.avatar_url || session.user.user_metadata.picture || '',
              sub: session.user.id
            };
            setUser(userData);
            setSession(session);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Handle redirect after OAuth callback
            const redirectTo = localStorage.getItem('redirectTo');
            if (redirectTo && redirectTo !== '/login') {
              localStorage.removeItem('redirectTo');
              logger.log('Redirecting after OAuth to:', redirectTo);
              navigate(redirectTo, { replace: true });
            } else if (location.pathname === '/login') {
              // Check if user came from magnet page via URL parameter (more reliable than localStorage)
              const urlParams = new URLSearchParams(location.search);
              const fromMagnetUrl = urlParams.get('fromMagnet');
              const fromMagnetStorage = localStorage.getItem('fromMagnetPage');
              
              logger.log('AuthContext (onAuthStateChange) - fromMagnet URL param:', fromMagnetUrl);
              logger.log('AuthContext (onAuthStateChange) - fromMagnetPage storage:', fromMagnetStorage);
              
              if (fromMagnetUrl === 'true' || fromMagnetStorage === 'true') {
                logger.log('AuthContext (onAuthStateChange) - Redirecting to PDF processor');
                localStorage.removeItem('fromMagnetPage');
                navigate('/invoice_processor/pdf', { replace: true });
              } else {
                logger.log('AuthContext (onAuthStateChange) - Redirecting to main processor');
                navigate('/invoice_processor', { replace: true });
              }
            }
          } catch (error) {
            logger.error('Error handling sign in:', error);
          }
        } else if (event === 'SIGNED_OUT' || !session) {
          setUser(null);
          setSession(null);
          localStorage.removeItem('user');
          localStorage.removeItem('redirectTo');
        } else if (event === 'TOKEN_REFRESHED' && session) {
          setSession(session);
        }
        
        // Set loading to false after any auth state change
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (session?.expires_at) {
      const timeUntilExpiry = (session.expires_at * 1000) - Date.now();
      const refreshBuffer = 5 * 60 * 1000; // 5 minutes before expiry
      
      if (timeUntilExpiry < refreshBuffer && timeUntilExpiry > 0) {
        logger.log('Auto-refreshing session token');
        supabase.auth.refreshSession().then(({ data, error }) => {
          if (error) {
            logger.error('Error refreshing session:', error);
            return;
          }
          if (data.session) {
            setSession(data.session);
            logger.log('Session refreshed successfully');
          }
        });
      }
    }
  }, [session]);

  const handleSignOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      localStorage.removeItem('user');
      localStorage.removeItem('redirectTo');

      // Then attempt to sign out from Supabase
      try {
        // Check if we have a valid session before attempting logout
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (currentSession) {
          await supabase.auth.signOut();
          logger.log('Successfully signed out from Supabase');
        } else {
          logger.log('No active session found, skipping Supabase logout');
        }
      } catch (signOutError: any) {
        // Handle specific error cases more gracefully
        if (signOutError?.status === 403 || signOutError?.message?.includes('403')) {
          logger.log('Session already expired or invalid, logout not needed');
        } else if (signOutError?.status === 401 || signOutError?.message?.includes('401')) {
          logger.log('Session already unauthorized, logout not needed');
        } else {
          logger.warn('Non-critical error during sign out:', signOutError);
        }
        // Continue even if sign out fails - we've already cleared local state
      }

      // Always navigate home
      navigate('/', { replace: true });
    } catch (error) {
      logger.error('Error during sign out:', error);
      // Even if everything fails, still navigate away
      navigate('/', { replace: true });
    }
  };

  const login = async (response: any) => {
    try {
      logger.log('Starting Google OAuth login...');
      
      // Store current location for redirect after login (but not if already on login page)
      const currentPath = location.pathname;
      if (currentPath !== '/login' && currentPath !== '/') {
        localStorage.setItem('redirectTo', currentPath);
        logger.log('Stored redirect path:', currentPath);
      }

      // Check if user came from magnet page and include it in the redirect URL
      const fromMagnetPage = localStorage.getItem('fromMagnetPage');
      let redirectUrl = `${window.location.origin}/login`;
      
      // Add magnet page flag as URL parameter to survive OAuth redirect
      if (fromMagnetPage === 'true') {
        redirectUrl = `${window.location.origin}/login?fromMagnet=true`;
        logger.log('OAuth redirect URL includes magnet flag:', redirectUrl);
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          skipBrowserRedirect: false
        },
      });

      if (error) {
        logger.error('OAuth error:', error);
        throw error;
      }

      logger.log('OAuth initiated successfully');
    } catch (error) {
      logger.error('Error during login:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      logger.log('Starting logout...');
      await handleSignOut();
      logger.log('Logout successful');
    } catch (error) {
      logger.error('Error during logout:', error);
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    try {
      logger.log('Starting email login...');
      
      // Validate input
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }
      
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors[0]);
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        logger.error('Email login error:', error);
        throw new Error(getAuthErrorMessage(error));
      }

      logger.log('Email login successful');
    } catch (error) {
      logger.error('Error during email login:', error);
      throw error;
    }
  };

  const registerWithEmail = async (email: string, password: string, name?: string) => {
    try {
      logger.log('Starting email registration...');
      
      // Validate input
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }
      
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors.join(', '));
      }

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            full_name: name?.trim() || '',
          },
          emailRedirectTo: `${window.location.origin}/login?verified=true`,
        },
      });

      if (error) {
        logger.error('Email registration error:', error);
        throw new Error(getAuthErrorMessage(error));
      }

      if (data.user && !data.session) {
        // User needs to verify email
        logger.log('Registration successful, email verification required');
      }

      logger.log('Email registration initiated successfully');
    } catch (error) {
      logger.error('Error during email registration:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      logger.log('Starting password reset...');
      
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password?token=true`,
      });

      if (error) {
        logger.error('Password reset error:', error);
        throw new Error(getAuthErrorMessage(error));
      }

      logger.log('Password reset email sent successfully');
    } catch (error) {
      logger.error('Error during password reset:', error);
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      logger.log('Starting password update...');
      
      if (!session) {
        throw new Error('No authenticated session');
      }
      
      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors.join(', '));
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        logger.error('Password update error:', error);
        throw new Error(getAuthErrorMessage(error));
      }

      logger.log('Password updated successfully');
    } catch (error) {
      logger.error('Error during password update:', error);
      throw error;
    }
  };

  const resendVerification = async (email: string) => {
    try {
      logger.log('Resending verification email...');
      
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/login?verified=true`,
        },
      });

      if (error) {
        logger.error('Resend verification error:', error);
        throw new Error(getAuthErrorMessage(error));
      }

      logger.log('Verification email resent successfully');
    } catch (error) {
      logger.error('Error resending verification email:', error);
      throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      logger.log('Starting account deletion...');
      
      if (!session?.user?.id) {
        throw new Error('No authenticated session');
      }

      // First, delete user's company details
      const { error: deleteDetailsError } = await supabase
        .from('user_company_details')
        .delete()
        .eq('user_id', session.user.id);

      if (deleteDetailsError) {
        logger.error('Error deleting company details:', deleteDetailsError);
        throw deleteDetailsError;
      }

      // Delete the user's account
      const { error: deleteUserError } = await supabase.rpc('delete_user');
      
      if (deleteUserError) {
        logger.error('Error deleting user account:', deleteUserError);
        throw deleteUserError;
      }

      // Clear local state and redirect
      await handleSignOut();
      logger.log('Account deletion successful');
    } catch (error) {
      logger.error('Error during account deletion:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isLoading, 
      login, 
      loginWithEmail,
      registerWithEmail,
      resetPassword,
      updatePassword,
      resendVerification,
      logout, 
      deleteAccount 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 